import { TranslatePipe } from "@ngx-translate/core";
import * as Sentry from "@sentry/angular";
import { AngularSvgIconModule } from "angular-svg-icon";

import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  input,
  signal,
  viewChild,
} from "@angular/core";

import { AccessPointType } from "../../../../../../apis/networkdashboard/graphql/graphql";
import { ApCountsByModel } from "../../../../../../apis/networkdashboard/rest/models";
import { summarizeAccessPoints } from "../../../../../../helpers/access-point-summary/access-point-summary";
import { slugify } from "../../../../../../helpers/slugify";

const FALLBACK_AP_IMAGES: Record<string, string> = {
  Aerohive: "assets/images/aps/fallback-aerohive.webp",
  Aruba: "assets/images/aps/fallback-aruba.webp",
};

@Component({
  selector: "access-points",
  templateUrl: "access-points.html",
  styleUrl: "access-points.scss",
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [AngularSvgIconModule, TranslatePipe],
})
export class AccessPoints implements AfterViewChecked {
  protected accessPoints = input.required<AccessPointType[]>();
  protected apCountsByModel = input.required<Record<string, ApCountsByModel>>();
  protected accessPointSummaries = computed(() => summarizeAccessPoints(this.accessPoints(), this.apCountsByModel()));

  /** The scroll area only renders when there are access points. */
  protected scrollarea = viewChild<ElementRef<HTMLElement>>("scrollarea");
  protected rightEnabled = signal(false);
  protected leftEnabled = signal(false);

  protected hasAccessPoints = computed(() => this.accessPointSummaries().length !== 0);

  ngAfterViewChecked() {
    this.checkScroll();
  }

  protected scrollLeft() {
    this.scrollBy(-150);
  }

  protected scrollRight() {
    this.scrollBy(150);
  }

  private scrollBy(offset: number) {
    const el = this.scrollarea()?.nativeElement;
    if (!el) {
      return;
    }
    el.scrollLeft += offset;
    setTimeout(() => this.checkScroll(), 500);
  }

  protected checkScroll() {
    const el = this.scrollarea()?.nativeElement;
    if (!el) {
      return;
    }
    this.leftEnabled.set(el.scrollLeft !== 0);
    this.rightEnabled.set(el.scrollWidth - (el.scrollLeft + el.clientWidth) !== 0);
  }

  protected getAccessPointImageUrl(model: string): string {
    return `assets/images/aps/${slugify(model)}.webp`;
  }

  protected onAccessPointImageError(event: Event) {
    if (!(event.target instanceof HTMLImageElement)) {
      return;
    }

    const imageElement = event.target;

    if (imageElement.dataset.fallbackApplied === "true") {
      return;
    }

    const brand = imageElement.dataset.brand ?? "";
    const fallbackImage = FALLBACK_AP_IMAGES[brand] ?? FALLBACK_AP_IMAGES["Aruba"];

    Sentry.captureMessage(`Missing access point image: ${imageElement.src}`, {
      level: "warning",
      tags: { feature: "access-point-images" },
    });

    imageElement.dataset.fallbackApplied = "true";
    imageElement.src = fallbackImage;
  }
}
